import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostBinding, Input, Output, Renderer2 } from '@angular/core';

import { viewService } from '@unpispas/upp-base';

/**
 * Full-viewport dialog overlay (backdrop + centered/edge-to-edge panel).
 *
 * The host is reparented to `document.body` on init so `position: fixed` always
 * covers the whole screen, regardless of any ancestor that establishes a CSS
 * containing block (Ionic's `ion-nav`, a scaled app body, a clipped split panel,
 * etc.) between the call site and the viewport.
 */
@Component({
    selector: 'upp-dialog',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-dialog.html',
    styleUrls: ['./upp-dialog.scss'],
})
export class UppDialogComponent implements AfterViewInit {
    @Input() backdropDismiss = true;

    @Output() OnClose = new EventEmitter<void>();

    constructor(
        public view: viewService,
        private el: ElementRef<HTMLElement>,
        private renderer: Renderer2,
    ) {}

    ngAfterViewInit(): void {
        if (typeof document !== 'undefined'){
            this.renderer.appendChild(document.body, this.el.nativeElement);
        }
    }

    @HostBinding('class.mobile')
    get IsMobile(): boolean {
        return this.view.Mobile === true;
    }

    @HostBinding('class.desktop')
    get IsDesktop(): boolean {
        return this.view.Desktop === true;
    }

    OnBackdropClick(): void {
        if (this.backdropDismiss){
            this.OnClose.emit();
        }
    }

    OnBackdropKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape'){
            event.preventDefault();
            this.OnClose.emit();
            return;
        }

        if (!this.backdropDismiss){
            return;
        }

        if (event.key === 'Enter' || event.key === ' '){
            event.preventDefault();
            this.OnClose.emit();
        }
    }

    OnPanelClick(event: Event): void {
        event.stopPropagation();
    }

    OnPanelKeydown(event: Event): void {
        event.stopPropagation();
    }
}

/**
 * Non-scrolling chrome region of `upp-dialog`: stays fixed (`flex: 0 0 auto`, sized
 * by `upp-dialog.scss`) while a sibling `upp-dialog-content` scrolls. Purely a layout
 * slot — colors, borders, padding and title markup stay the caller's responsibility,
 * mirroring `upp-panel-header`.
 */
@Component({
    selector: 'upp-dialog-header',
    template: `<ng-content></ng-content>`,
    styleUrls: [],
})
export class UppDialogHeaderComponent {
    // nothing to do
}

/**
 * Scrollable body region of `upp-dialog`: wraps projected content in `upp-scrollable`
 * so callers no longer need to hand-roll the flex/overflow chrome that keeps a header
 * and footer fixed while the body scrolls.
 */
@Component({
    selector: 'upp-dialog-content',
    template: `
        <upp-scrollable [scrollbar]="scrollbar">
            <ng-content></ng-content>
        </upp-scrollable>
    `,
    styleUrls: [],
})
export class UppDialogContentComponent {
    /** Forwarded to the internal `upp-scrollable`; see its `scrollbar` input for semantics. */
    @Input() scrollbar: 'none' | 'x' | 'y' | 'both' = 'y';
}

/**
 * Non-scrolling chrome region of `upp-dialog`: stays fixed (`flex: 0 0 auto`, sized
 * by `upp-dialog.scss`) while a sibling `upp-dialog-content` scrolls. Purely a layout
 * slot — colors, borders, padding and action markup stay the caller's responsibility,
 * mirroring `upp-panel-footer`.
 */
@Component({
    selector: 'upp-dialog-footer',
    template: `<ng-content></ng-content>`,
    styleUrls: [],
})
export class UppDialogFooterComponent {
    // nothing to do
}
